// KPI dlaždice.
//
// 13.9.2026 (fáze 4i): kreslí totéž co `Dlazdice` ze vzorníku, tedy barevný
// proužek nahoře, popisek drobnými velkými písmeny a velké číslo. Dřív to byl
// bílý box s barevným rámečkem kolem ikony a doplňky jako drobný šedý text,
// takže se dlaždice na stránkách s novým vzhledem nepotkávaly s ostatními.
//
// Props zůstávají stejné (`label`, `value`, `suffix`, `delta`, `deltaLabel`,
// `icon`, `accent`, `sub`, `badge`, `children`), takže se nemuselo sáhnout do
// stránek, které dlaždici používají. `icon` se nekreslí: barvu nese proužek
// a ikona v malé dlaždici jen ubírala místo číslu.

import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { fmt } from "../lib/format.js";

// Původní názvy odstínů zůstávají, jen ukazují na proužky ze vzorníku.
const PRUHY = {
  blue: "bg-sky-500",
  green: "bg-emerald-500",
  amber: "bg-brand-gold",
  purple: "bg-brand",
  brand: "bg-brand",
  violet: "bg-brand",
  red: "bg-rose-500",
  slate: "bg-slate-400",
};

const HODNOTA = {
  green: "text-emerald-700",
  red: "text-rose-700",
};

/** Pilulka s doplňkovým údajem pod číslem. */
function Doplnek({ children, ton }) {
  const tony = {
    ok: "bg-emerald-100 text-emerald-700",
    problem: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${tony[ton] || "bg-slate-100 text-slate-600"}`}>
      {children}
    </span>
  );
}

export default function KpiCard({
  label,
  value,
  suffix,
  delta,
  deltaLabel,
  icon: Ikona,
  accent = "blue",
  sub,
  badge,
  children,
}) {
  // `Ikona` se schválně nekreslí, prop zůstává jen kvůli stránkám, které ho
  // pořád posílají.
  void Ikona;
  const roste = delta !== undefined && delta > 0;
  // `sub` bývá buď krátký text, a pak z něj je pilulka, nebo si stránka posílá
  // vlastní obsah, třeba svoje pilulky pod sebou. Ten se kreslí tak, jak přišel.
  // Dřív se obalil taky do pilulky a kolem vlastních pilulek vznikl velký šedý
  // tvar (14.9.2026).
  const subJeText = typeof sub === "string" || typeof sub === "number";
  return (
    <div data-kpi className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glass">
      <div className={`h-1.5 ${PRUHY[accent] || PRUHY.blue}`} />
      <div className="p-3">
        <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {label}
        </div>
        <div className={`mt-0.5 break-words text-xl font-bold tabular-nums sm:text-2xl ${HODNOTA[accent] || "text-slate-900"}`}>
          {typeof value === "number" ? fmt(value) : value}
          {suffix && <span className="ml-1 text-base font-normal text-slate-500">{suffix}</span>}
        </div>

        {(badge || delta !== undefined || (sub && subJeText)) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {badge && <Doplnek ton="ok">{badge}</Doplnek>}
            {delta !== undefined && (
              <Doplnek ton={roste ? "ok" : "problem"}>
                {roste ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(delta)} %{deltaLabel ? ` ${deltaLabel}` : ""}
              </Doplnek>
            )}
            {sub && subJeText && <Doplnek><span className="demo-sensitive">{sub}</span></Doplnek>}
          </div>
        )}

        {sub && !subJeText && <div className="mt-2">{sub}</div>}

        {children}
      </div>
    </div>
  );
}
